import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { BankAccountDetail } from '@/features/accounting-bank/bank-account-detail'
import { hasGrant } from '@/lib/me'

const FEED_VIEWS = ['pending', 'posted', 'excluded'] as const
type FeedView = (typeof FEED_VIEWS)[number]

/** Never throws: a bad value in a shared URL falls back instead of an error screen. */
const bankAccountSearch = (
  search: Record<string, unknown>,
): { tab?: string; feedView?: FeedView; feedPage?: number } => ({
  tab: typeof search.tab === 'string' ? search.tab : undefined,
  feedView: FEED_VIEWS.find((view) => view === search.feedView) ?? 'pending',
  feedPage:
    typeof search.feedPage === 'number' && Number.isInteger(search.feedPage) && search.feedPage >= 1
      ? search.feedPage
      : 1,
})

export const Route = createFileRoute('/_authenticated/accounting/bank-accounts/$accountId/')({
  validateSearch: bankAccountSearch,
  beforeLoad: ({ context }) => {
    if (!hasGrant(context.queryClient, 'accounting_bank.view')) {
      throw redirect({ to: '/accounting/bank-accounts' })
    }
  },
  component: BankAccountDetailRoute,
})

function BankAccountDetailRoute() {
  const navigate = useNavigate({ from: Route.fullPath })
  const { accountId } = Route.useParams()
  const { tab, feedView, feedPage } = Route.useSearch()

  return (
    <BankAccountDetail
      accountId={accountId}
      tab={tab}
      feedView={feedView}
      feedPage={feedPage}
      onTabChange={(next) =>
        void navigate({
          search: (previous) => ({ ...previous, tab: next }),
          replace: true,
        })
      }
      onFeedViewChange={(next) =>
        void navigate({
          search: (previous) => ({ ...previous, feedView: next, feedPage: 1 }),
        })
      }
      onFeedPageChange={(next) =>
        void navigate({
          search: (previous) => ({ ...previous, feedPage: next }),
        })
      }
      onFeedImportComplete={() =>
        void navigate({
          search: (previous) => ({ ...previous, tab: 'feed', feedView: 'pending', feedPage: 1 }),
        })
      }
    />
  )
}
